import { ArrowRight, Search } from 'lucide-react';
import { IContact } from 'types';
import AttributeValue from './AttributeValue';
import { Link } from 'react-router-dom';

const Overview = ({ contact }: { contact: IContact }) => {
  return (
    <div>
      <div>
        <div className="bg-white rounded-lg shadow-sm px-6 py-6 max-w-xl border border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Attributes</h2>
            <Link
              to={`/contacts/edit/${contact._id}`}
              aria-label="View all attributes"
              className="inline-flex items-center justify-center rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-muted-foreground hover:text-foreground hover:bg-muted focus:outline-none focus:ring-0 px-4 py-2 text-sm flex items-center gap-1"
            >
              Edit Attributes
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Search */}
          <div className="mb-6 relative">
            <label htmlFor="attribute-search" className="sr-only">
              Search attributes
            </label>
            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400 pointer-events-none">
              <Search className="w-4 h-4" aria-hidden="true" />
            </span>

            <input
              id="attribute-search"
              type="text"
              placeholder="Search attributes..."
              className="w-full rounded-lg border border-gray-200 bg-gray-50 pl-9 pr-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-0 transition-colors"
            />
          </div>

          {/* Identifiers */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <div className="uppercase tracking-wide text-xs text-gray-500 font-semibold mb-3">
              Identifiers
            </div>
            <dl className="space-y-3">
              <div className="flex items-center justify-between">
                <dt className="text-sm font-medium text-gray-600">Manage</dt>
                <dd>
                  <button
                    className="text-muted-foreground hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 rounded-md p-1"
                    aria-label="Manage contact"
                  >
                    <span aria-hidden="true">→</span>
                  </button>
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm font-medium text-gray-600">Custom ID</dt>
                <dd className="text-sm text-gray-900">
                  {contact?.custom_id || (
                    <span className="text-muted-foreground italic">N/A</span>
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm font-medium text-gray-600">Email</dt>
                <dd className="text-sm text-gray-900">
                  {contact?.email || (
                    <span className="text-muted-foreground italic">N/A</span>
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-sm font-medium text-gray-600">
                  Created At
                </dt>
                <dd className="text-sm text-gray-900">
                  {contact?.createdAt || (
                    <span className="text-muted-foreground italic">N/A</span>
                  )}
                </dd>
              </div>
            </dl>
          </div>

          {/* Attributes */}
          <div className="space-y-0">
            <div className="uppercase tracking-wide text-xs text-gray-500 font-semibold mb-3">
              Attributes
            </div>
            {contact?.attributes &&
            Object.keys(contact.attributes).length > 0 ? (
              <div className="divide-y divide-gray-100">
                {Object.entries(contact.attributes).map(
                  ([key, value], index) => {
                    const isComplexType =
                      Array.isArray(value) ||
                      (typeof value === 'object' && value !== null);

                    return (
                      <div
                        key={key}
                        className={`py-3 first:pt-0 last:pb-0 transition-colors hover:bg-gray-50/50 rounded-md -mx-2 px-2 ${
                          isComplexType ? 'flex flex-col gap-2' : ''
                        }`}
                      >
                        {!isComplexType ? (
                          <div className="flex items-start justify-between gap-4">
                            <dt className="text-sm font-medium text-gray-600 min-w-0 flex-shrink-0">
                              {key}
                            </dt>
                            <dd className="text-sm text-gray-900 text-right min-w-0 flex-1">
                              <AttributeValue value={value} />
                            </dd>
                          </div>
                        ) : (
                          <div className="w-full">
                            <dd className="flex-1 min-w-0">
                              <AttributeValue value={value} keyName={key} />
                            </dd>
                          </div>
                        )}
                      </div>
                    );
                  }
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No attributes available
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
